/**
 * What the basket on the right costs, pays and risks — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE RECOMPUTE PER CADENCE, NOT ONE PER TICK
 *
 * Ten legs against a live ladder would otherwise re-solve the margin scan
 * several times a second for a panel a human reads at human speed. The same
 * rule §4.7 sets for the strategy previews, and the same tick the greeks pass
 * already runs on — so the margin and the volatilities it reads are from one
 * moment rather than two.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CALENDAR IS PRICED, NOT REFUSED — ROADMAP 9.67
 *
 * The first cut drew nothing for a basket spanning two expiries and said so
 * in four places. That was correct about `payoffShape` — it settles every leg
 * on one day, which is a wrong claim for a calendar — and wrong about the
 * trader, who can see the shape of a diagonal nowhere else.
 *
 * So there are two paths, and which one runs is decided by the basket:
 *
 *   ONE EXPIRY    `payoffShape`. Exact, piecewise linear, needs no
 *                 volatility at all, and cannot disagree with the order that
 *                 follows it.
 *   SEVERAL       `calendarShape`. The curve on the NEAREST expiry, with the
 *                 later legs revalued at their own IV — a model valuation,
 *                 labelled with the date it is drawn for.
 *
 * Exact where exact is possible; a named model where it is not. The one
 * thing not done is pretending the second is the first.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BASKET SUBSCRIBES ITS OWN LEGS
 *
 * A draft may hold a strike on an expiry the ladder is not showing — that is
 * the whole point of a saved basket — and only the visible window is
 * subscribed. Without this the one leg the trader cannot see is the only one
 * with no price, and the basket reports itself unpriceable. Refcounted like
 * every other subscription, so closing the builder gives them back.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useMemo, useState } from 'react'
import {
  calendarShape,
  istDateOf,
  netPremiumOf,
  payoffShape,
  valueAtHorizon,
  type MarginEstimate,
  type PayoffLeg,
  type PayoffShape,
  type ResolvedLeg,
} from '@apna/shared'
import { tickStore } from '@/feed/tickStore'
import { useTickSubscription } from '@/feed/useTick'
import {
  GREEKS_CADENCE_MS,
  RISK_FREE_RATE,
  type ChainGreeks,
} from '@/screens/terminal/useChainGreeks'
import { payoffLegsOf } from '@/screens/terminal/previewPayoff'
import type { PayoffModel } from '@/components/payoff/payoffView'
import type { BasketLegDto } from '@/types/basket'
import { resolvedLegsOf, soleExpiry } from '../utils/basketPreview'
import { basketMargin, solveBasket } from '../utils/basketRisk'

export interface BasketPreview {
  /** The legs as the order path sees them, priced at the crossing touch. */
  legs: readonly ResolvedLeg[]
  /** Rupees collected (positive) or paid (negative). Null when unpriceable. */
  netPremium: number | null
  /** Why it could not be priced, when it could not. */
  premiumWhy: string | null
  /** Signed, lot-expanded legs for the chart. Null when a leg has no price. */
  payoffLegs: readonly PayoffLeg[] | null
  /** Max profit, max loss and breakevens — exact, or on the nearest expiry. */
  shape: PayoffShape | null
  /**
   * The valuation the chart should draw INSTEAD of settlement, for a basket
   * spanning expiries. Null when every leg expires together, and the chart
   * then draws the exact at-expiry curve from the legs themselves.
   */
  model: PayoffModel | null
  /** The initial margin this basket would block. Null when it cannot be scanned. */
  margin: MarginEstimate | null
  /** The single expiry the basket trades, or null when it spans several. */
  expiry: string | null
  /** The date the payoff is drawn FOR — the nearest expiry. */
  horizon: string | null
  /** How many distinct expiries the margin was scanned over. */
  marginExpiries: number
  /** Why there is no margin figure, in a sentence the panel can show. */
  marginWhy: string | null
  /** Why there is no payoff, when there is none. */
  shapeWhy: string | null
}

export interface BasketPreviewInput {
  legs: readonly BasketLegDto[]
  lotSize: number
  /** The chain's solved volatilities — the SAME pass the greeks column uses. */
  greeks: ChainGreeks
  /** Which expiry those volatilities were solved for. */
  greeksExpiry: string | null
  spot: number
  enabled: boolean
}

export function useBasketPreview({
  legs,
  lotSize,
  greeks,
  greeksExpiry,
  spot,
  enabled,
}: BasketPreviewInput): BasketPreview {
  const tokens = useMemo(
    () => (enabled ? [...new Set(legs.map((l) => l.token))] : []),
    [enabled, legs],
  )
  useTickSubscription(tokens)

  /*
   * A tick counter rather than a dependency on the store, for the reason the
   * greeks hook keeps one: the prices live outside React and nothing here
   * should re-render on a frame nobody is reading.
   */
  const [pulse, setPulse] = useState(0)
  useEffect(() => {
    if (!enabled) return
    const t = window.setInterval(() => setPulse((n) => n + 1), GREEKS_CADENCE_MS)
    return () => window.clearInterval(t)
  }, [enabled])

  return useMemo((): BasketPreview => {
    void pulse
    const expiry = soleExpiry(legs)
    const horizon = legs.length === 0 ? null : [...legs].sort((a, b) => (a.expiry < b.expiry ? -1 : 1))[0]!.expiry

    const nothing: BasketPreview = {
      legs: [],
      netPremium: null,
      premiumWhy: null,
      payoffLegs: null,
      shape: null,
      model: null,
      margin: null,
      expiry,
      horizon,
      marginExpiries: 0,
      marginWhy: null,
      shapeWhy: null,
    }
    if (legs.length === 0 || !(lotSize > 0)) return nothing

    const resolved = resolvedLegsOf(legs, (t) => tickStore.snapshot(t))
    const priced = netPremiumOf(resolved, lotSize)
    const payoffLegs = payoffLegsOf(resolved, lotSize)

    /*
     * ONE SOLVE FEEDS BOTH the margin scan and the calendar curve, so the
     * volatility behind "max loss ₹4L" is the same one behind "margin
     * ₹7L". Two solves would be two surfaces describing one basket.
     */
    const solved = solveBasket({
      legs,
      resolved,
      lotSize,
      spot,
      chainExpiry: greeksExpiry,
      greeks,
    })

    let shape: PayoffShape | null = null
    let model: PayoffModel | null = null
    let shapeWhy: string | null = null

    if (expiry !== null) {
      /*
       * EXACT. Every leg settles on the same day, so the payoff is piecewise
       * linear and `payoffShape` solves it at the kinks — no sampling, no
       * volatility, and nothing that can drift from the order.
       */
      if (payoffLegs) shape = payoffShape(payoffLegs)
      else shapeWhy = priced.ok ? 'Waiting for prices…' : priced.why
    } else if (solved.ok) {
      // A MODEL VALUATION on the nearest expiry. See the header.
      const input = { legs: solved.legs, spot, rate: RISK_FREE_RATE }
      const calendar = calendarShape(input)
      shape = calendar
      model = {
        valueAt: (s: number) => valueAtHorizon(input, s, calendar.horizonYears),
        breakevens: calendar.breakevens,
      }
    } else {
      shapeWhy = solved.why
    }

    /*
     * MARGIN IS ONE SCAN PER EXPIRY, ADDED UP — see `basketRisk.ts`. The
     * exchange gives a calendar spread a credit this does not, so the figure
     * is an upper bound, which is the safe direction to be wrong in.
     */
    const margin = solved.ok
      ? basketMargin(solved.legs, {
          spot,
          /*
           * ASKED OF THE DATE, not of the ladder. The exchange's extra 2%
           * on short index options applies to legs expiring TODAY, and a
           * basket can hold one of those while the chain shows next month
           * — reading the ladder's own flag would have missed exactly that
           * leg, on the one day it costs the most.
           */
          isExpiryDay: (e) => e === istDateOf(Date.now()),
        })
      : null
    const marginWhy = !solved.ok ? solved.why : margin && !margin.ok ? margin.why : null

    return {
      legs: resolved,
      netPremium: priced.ok ? priced.netPremium : null,
      premiumWhy: priced.ok ? null : priced.why,
      payoffLegs,
      shape,
      model,
      margin,
      expiry,
      horizon,
      marginExpiries: margin && margin.ok ? (margin.expiries ?? 1) : 0,
      marginWhy,
      shapeWhy,
    }
  }, [pulse, legs, lotSize, greeks, greeksExpiry, spot])
}
